import { useEffect, useState, useRef } from 'react';
import { api, ApiError } from '../lib/api';
import { Stat } from '../components/Stat';
import { Skel } from '../components/Skel';
import { Banner } from '../components/Banner';
import { EmptyState } from '../components/EmptyState';
import { RiskBadge } from '../components/RiskBadge';
import type { SuggestItem } from '../data/types';

interface DashboardData {
  companies: { total: string; active: string; risky?: string };
  sanctions: { total: string; active: string };
  tax_summary?: { total_records: string; with_debt: string } | null;
  generated_at: string;
}

interface TopCompany {
  slug: string;
  inn: string;
  name_short: string;
  region_name: string | null;
  okved_main_name?: string | null;
  risk_level: string | null;
  risk_score: number | null;
  last_revenue: string | null;
}

/**
 * Главная страница ProfileCompany. Foundation версия для Vite-миграции.
 *
 * Структура:
 *  1. Hero с поиском (autocomplete через /v1/companies/suggest)
 *  2. 4 статистики (companies / sanctions / tax_summary / generated_at)
 *  3. Top-12 компаний по выручке
 *
 * Ассеssibility: search input с aria-autocomplete=list, aria-expanded,
 * aria-controls. Suggest items ↑/↓ keyboard nav (на TODO для C2-followup).
 *
 * Mobile-first: minmax(0,1fr) в grid, padding clamp().
 */
export function Home() {
  const [dashboard, setDashboard] = useState<DashboardData | null>(null);
  const [top, setTop] = useState<TopCompany[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<SuggestItem[]>([]);
  const [suggestOpen, setSuggestOpen] = useState(false);
  const debounceRef = useRef<number | null>(null);

  useEffect(() => {
    const ac = new AbortController();
    Promise.all([
      api.dashboard({ signal: ac.signal }) as Promise<DashboardData>,
      api.top(12, { signal: ac.signal }) as Promise<{ items: TopCompany[] }>,
    ])
      .then(([d, t]) => {
        setDashboard(d);
        setTop(t.items);
      })
      .catch((e) => {
        if (e.name !== 'AbortError') {
          setError(e instanceof ApiError ? `API ${e.status}` : String(e));
        }
      });
    return () => ac.abort();
  }, []);

  // Debounced suggest
  useEffect(() => {
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    if (query.trim().length < 2) {
      setSuggestions([]);
      return;
    }
    debounceRef.current = window.setTimeout(() => {
      const ac = new AbortController();
      api
        .suggest(query.trim(), 10, { signal: ac.signal })
        .then((r) => setSuggestions(r.items))
        .catch(() => {});
    }, 250);
    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, [query]);

  return (
    <main className="container" style={{ paddingTop: 'clamp(32px, 6vw, 64px)' }}>
      {/* HERO */}
      <section className="home-hero" style={{ textAlign: 'center', marginBottom: 48 }}>
        <h1 style={{ fontSize: 'clamp(28px, 4vw, 44px)' }}>
          Проверка контрагентов в РФ
        </h1>
        <p
          className="subtitle"
          style={{ fontSize: 'clamp(15px, 1.5vw, 18px)', maxWidth: 640, margin: '0 auto 32px' }}
        >
          {dashboard
            ? `${Number(dashboard.companies.total).toLocaleString('ru-RU')} компаний, ${Number(
                dashboard.sanctions.active
              ).toLocaleString('ru-RU')} активных санкций. Только из открытых государственных источников.`
            : 'Только из открытых государственных источников.'}
        </p>

        {/* SEARCH */}
        <div
          className="home-search"
          style={{
            position: 'relative',
            maxWidth: 600,
            margin: '0 auto',
          }}
          role="combobox"
          aria-haspopup="listbox"
          aria-expanded={suggestOpen && suggestions.length > 0}
          aria-controls="home-suggest"
        >
          <input
            type="search"
            placeholder="ИНН, ОГРН или название компании"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => setSuggestOpen(true)}
            onBlur={() => window.setTimeout(() => setSuggestOpen(false), 200)}
            aria-label="Поиск компании по ИНН, ОГРН или названию"
            aria-autocomplete="list"
            style={{
              width: '100%',
              padding: '14px 18px',
              fontSize: 16,
              border: '1px solid var(--border, #e6e2d6)',
              borderRadius: 12,
              background: 'var(--surface, #ffffff)',
              minWidth: 0,
            }}
          />
          {suggestOpen && suggestions.length > 0 && (
            <ul
              id="home-suggest"
              role="listbox"
              style={{
                position: 'absolute',
                top: 'calc(100% + 4px)',
                left: 0,
                right: 0,
                listStyle: 'none',
                margin: 0,
                padding: 6,
                background: 'var(--surface, #ffffff)',
                border: '1px solid var(--border, #e6e2d6)',
                borderRadius: 12,
                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
                textAlign: 'left',
                zIndex: 10,
                maxHeight: 360,
                overflowY: 'auto',
              }}
            >
              {suggestions.map((s) => (
                <li
                  key={s.slug}
                  role="option"
                  aria-selected="false"
                  style={{ padding: '10px 12px', cursor: 'pointer', borderRadius: 8 }}
                  onMouseEnter={(e) =>
                    ((e.target as HTMLLIElement).style.background = 'var(--bg, #fbfaf7)')
                  }
                  onMouseLeave={(e) =>
                    ((e.target as HTMLLIElement).style.background = 'transparent')
                  }
                  tabIndex={0}
                  onClick={() => {
                    window.location.hash = `#/company/${s.slug}`;
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      window.location.hash = `#/company/${s.slug}`;
                    }
                  }}
                >
                  <div style={{ fontWeight: 600, overflowWrap: 'anywhere' }}>{s.name_short}</div>
                  <div style={{ fontSize: 13, color: 'var(--muted, #6b7287)' }}>
                    ИНН {s.inn}
                    {s.region_name && ` · ${s.region_name}`}
                    {s.status !== 'active' && ` · ${s.status}`}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      {/* STATS */}
      <section aria-label="Статистика базы">
        {error && <Banner tone="risk" title="Ошибка загрузки">{error}</Banner>}
        {!dashboard && !error && (
          <div className="stats">
            <Skel height={88} radius={12} />
            <Skel height={88} radius={12} />
            <Skel height={88} radius={12} />
            <Skel height={88} radius={12} />
          </div>
        )}
        {dashboard && (
          <div className="stats">
            <Stat label="Компаний в базе" value={dashboard.companies.total} />
            <Stat
              label="Активных компаний"
              value={dashboard.companies.active}
              note={
                dashboard.companies.risky
                  ? `${Number(dashboard.companies.risky).toLocaleString('ru-RU')} высокорисковых`
                  : undefined
              }
            />
            <Stat
              label="Активных санкций"
              value={dashboard.sanctions.active}
              note={`${Number(dashboard.sanctions.total).toLocaleString('ru-RU')} всего`}
            />
            {dashboard.tax_summary && (
              <Stat
                label="Записей о налогах"
                value={dashboard.tax_summary.total_records}
                note={`${Number(dashboard.tax_summary.with_debt).toLocaleString('ru-RU')} с задолженностью`}
              />
            )}
          </div>
        )}
      </section>

      {/* TOP COMPANIES */}
      <section aria-label="Топ компаний по выручке" style={{ marginTop: 48 }}>
        <h2
          style={{
            fontFamily: 'Fraunces, Georgia, serif',
            fontSize: 'clamp(20px, 2.5vw, 26px)',
            margin: '0 0 16px',
            color: 'var(--navy-700, #0b1736)',
          }}
        >
          Топ компаний по выручке
        </h2>
        {!top && (
          <div style={{ display: 'grid', gap: 12 }}>
            <Skel count={5} height={64} radius={12} />
          </div>
        )}
        {top && top.length === 0 && (
          <EmptyState
            icon="📋"
            title="Список временно недоступен"
            body="Попробуйте обновить страницу или зайти позже."
          />
        )}
        {top && top.length > 0 && (
          <ul
            style={{
              listStyle: 'none',
              padding: 0,
              margin: 0,
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: 12,
            }}
          >
            {top.map((c) => (
              <li
                key={c.slug}
                style={{
                  background: 'var(--surface, #ffffff)',
                  border: '1px solid var(--border, #e6e2d6)',
                  borderRadius: 12,
                  padding: 16,
                  minWidth: 0,
                }}
              >
                <a
                  href={`#/company/${c.slug}`}
                  style={{
                    fontWeight: 600,
                    color: 'var(--navy-700, #0b1736)',
                    textDecoration: 'none',
                    overflowWrap: 'anywhere',
                    display: 'block',
                  }}
                >
                  {c.name_short}
                </a>
                <div
                  style={{
                    fontSize: 13,
                    color: 'var(--muted, #6b7287)',
                    margin: '6px 0',
                    overflowWrap: 'anywhere',
                  }}
                >
                  ИНН {c.inn}
                  {c.region_name && ` · ${c.region_name}`}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <RiskBadge
                    level={c.risk_level as 'low' | 'moderate' | 'elevated' | 'high' | null}
                    score={c.risk_score}
                  />
                  {c.last_revenue && (
                    <span style={{ fontSize: 13, color: 'var(--muted, #6b7287)' }}>
                      Выручка {formatRevenueShort(c.last_revenue)}
                    </span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* TOOLS */}
      <section
        style={{
          marginTop: 32,
          display: 'flex',
          gap: 10,
          flexWrap: 'wrap',
          justifyContent: 'center',
        }}
      >
        <a
          href="#/fssp-check"
          style={{
            padding: '10px 18px',
            fontSize: 14,
            fontWeight: 600,
            border: '1px solid var(--border, #e6e2d6)',
            borderRadius: 10,
            color: 'var(--navy-700, #0b1736)',
            textDecoration: 'none',
            background: 'var(--surface, #fff)',
          }}
        >
          ⚖️ Проверка по ФССП
        </a>
      </section>

      {/* GENERATED AT */}
      {dashboard && (
        <p
          className="meta"
          style={{
            color: 'var(--muted, #6b7287)',
            fontSize: 12,
            marginTop: 32,
            textAlign: 'center',
          }}
        >
          Данные обновлены: {new Date(dashboard.generated_at).toLocaleString('ru-RU')}
        </p>
      )}
    </main>
  );
}

function formatRevenueShort(value: string): string {
  const n = Number(value);
  if (!Number.isFinite(n)) return value;
  if (n >= 1_000_000_000_000) return `${(n / 1_000_000_000_000).toFixed(1)} трлн ₽`;
  if (n >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(1)} млрд ₽`;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)} млн ₽`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)} тыс ₽`;
  return `${n.toLocaleString('ru-RU')} ₽`;
}
